import './styles.scss'

import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Table } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2becTable from '@/components/B2becTable'
import FormatPrice from '@/components/FormatPrice'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectCartData,
  selectCheckoutSetting,
  selectHasMoqProduct,
  selectIsLoadingAfterRedeemingVoucher,
} from '@/store/selectors/cartSelector'
import { selectHideVoucher } from '@/store/selectors/configSelector'
import { replaceCartItems, setCartItemQuantity } from '@/store/slices/cartSlice'
import useDummyItems from '@/hooks/useDummyItems'
import usePermission from '@/hooks/usePermission'
import {
  MODULE_CART_CHECKOUT_RIGHTS,
  MODULE_SHOP_NAVIGATION_RIGHTS,
} from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import tableColumns from './TableColumns'

interface CartItemTableProps {
  isLoading?: boolean
  cartDataValue?: unknown
  isInsideSupportArea?: boolean
}

const CartItemTable = ({
  isLoading = false,
  cartDataValue,
  isInsideSupportArea = false,
}: CartItemTableProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isLoadingAfterRedeemingVoucher = useAppSelector(
    selectIsLoadingAfterRedeemingVoucher,
  )
  const cartDataFromStore = useAppSelector(selectCartData)
  const cartData = !isInsideSupportArea ? cartDataFromStore : cartDataValue
  const hideVoucher = useAppSelector(selectHideVoucher)
  const shouldHideCartTotalPrice = useAppSelector(selectHasMoqProduct)
  const { hasPermission: hasDeleteProductFromCartPermission } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.DELETE_PRODUCT_FROM_CART,
  )
  const { hasPermission: hasViewIndividualPricePermission } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.VIEW_INDIVIDUAL_PRICE,
  )

  const checkoutSettingFromStore = useAppSelector(selectCheckoutSetting)
  const {
    includingVAT: isIncludingVAT,
    headLvlConditions: headLevelConditions,
    positionLvlConditions: shouldShowRecyclingFee,
  } = !isInsideSupportArea
    ? checkoutSettingFromStore
    : cartDataValue?.checkoutSettings

  const { items: cartItems, voucherValue } = cartData

  const cartDataSource = useMemo(
    () =>
      cartItems.map((item) => ({
        ...item,
        disabled: item?.error && item?.displayAddToCart,
      })),
    [cartItems],
  )

  const shouldDisplayVoucherLevel = useCallback(
    ({ discount }) => {
      if (hideVoucher) return false

      let isApplied = false
      if (voucherValue) {
        const { categoryIds, materialNumbers } = voucherValue
        if (!isValidArray(categoryIds) && !isValidArray(materialNumbers)) {
          return false
        }
        isApplied = discount && +discount > 0
      }
      return isApplied
    },
    [voucherValue, hideVoucher],
  )

  const fakeProducts = useDummyItems(3)

  const handleCellClick = (materialNumber) => {
    if (materialNumber && typeof materialNumber === 'string') {
      navigate(linkGenerator(`/product/${materialNumber}`))
    }
  }

  const confirmReplacementHandler = useCallback(
    (oldMaterialNumber, replacedMaterialNumber, quantity) => {
      dispatch(
        replaceCartItems({
          oldMaterialNumber,
          replacedMaterialNumber,
          quantity,
        }),
      )
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [dispatch],
  )

  const confirmMoqHandler = useCallback(
    (e, record) => {
      e.stopPropagation()
      dispatch(setCartItemQuantity(record))
    },
    [dispatch],
  )

  const tableRowClasses = (record) => {
    if (record?.oldMaterialNumber || record?.originalMaterial) {
      return 'border-section--lower'
    }
    if (record?.replacedByMaterialNumber || record?.bonusMaterial) {
      return 'border-section--upper'
    }
    if (record?.hasMinQuantity) {
      return 'minimum-quantity-section'
    }

    if (record?.error && record?.displayAddToCart) {
      return 'error-row'
    }
    // normal product
    return ''
  }

  const cartRowKey = (record) => {
    if (record?.oldMaterialNumber) {
      return `${record?.materialNumber}-${record?.oldMaterialNumber}`
    }
    return [record?.materialNumber, record?.itemCateg].join('|')
  }

  const renderAppliedVoucher = useCallback(() => {
    const { code: voucherCode, totalDiscount } = voucherValue || {}

    return voucherCode
      ? (
        <Table.Summary.Row className="total-section">
          <Table.Summary.Cell colSpan={5}>
            <div>{t('voucher.fields.voucherCode')}</div>
            <div>{voucherCode}</div>
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {isLoading || isLoadingAfterRedeemingVoucher ? <Skeleton /> : (
              <FormatPrice
                className="voucher-code"
                currencyUnit={cartData?.currencyUnit}
                price={totalDiscount}
              />
            )}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )
      : null
  }, [
    t,
    isLoading,
    voucherValue,
    cartData.currencyUnit,
    isLoadingAfterRedeemingVoucher,
  ])

  const tableSummary = () => {
    if (!hasViewIndividualPricePermission) {
      return null
    }
    return (
      <>
        {!!headLevelConditions && (
          <Table.Summary.Row>
            <Table.Summary.Cell colSpan={5}>
              <span>{t('cart.table.additionalCost')}</span>
            </Table.Summary.Cell>
            <Table.Summary.Cell>
              {isLoading || isLoadingAfterRedeemingVoucher ? <Skeleton /> : (
                <FormatPrice
                  currencyUnit={cartData?.currencyUnit}
                  price={cartData?.additionalCost}
                />
              )}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}

        <Table.Summary.Row>
          <Table.Summary.Cell colSpan={5}>
            <span>{t('cart.table.subtotal')}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {isLoading || isLoadingAfterRedeemingVoucher
              ? <Skeleton />
              : !shouldHideCartTotalPrice
              ? (
                <FormatPrice
                  currencyUnit={cartData?.currencyUnit}
                  price={cartData?.cartSubTotal}
                />
              )
              : null}
          </Table.Summary.Cell>
        </Table.Summary.Row>

        <Table.Summary.Row>
          <Table.Summary.Cell colSpan={5}>
            <span>{t('cart.table.shippingCosts')}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {isLoading || isLoadingAfterRedeemingVoucher ? <Skeleton /> : (
              <FormatPrice
                currencyUnit={cartData?.currencyUnit}
                price={cartData?.shippingCosts}
              />
            )}
          </Table.Summary.Cell>
        </Table.Summary.Row>
        <Table.Summary.Row className="total-section">
          <Table.Summary.Cell className="boldText" colSpan={5}>
            <span>{t('total')}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="boldText">
            {isLoading || isLoadingAfterRedeemingVoucher
              ? <Skeleton />
              : !shouldHideCartTotalPrice
              ? (
                <FormatPrice
                  currencyUnit={cartData?.currencyUnit}
                  price={cartData?.cartTotal}
                />
              )
              : null}
          </Table.Summary.Cell>
        </Table.Summary.Row>
        {!hideVoucher && renderAppliedVoucher()}
        {/* show vat and total with vat */}
        {isIncludingVAT && (
          <>
            <Table.Summary.Row className="total-section vat">
              <Table.Summary.Cell className="boldText" colSpan={5}>
                <span>{t('vat')}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell className="boldText">
                {isLoading || isLoadingAfterRedeemingVoucher ? <Skeleton /> : (
                  <FormatPrice
                    currencyUnit={cartData?.currencyUnit}
                    price={cartData?.vat}
                  />
                )}
              </Table.Summary.Cell>
            </Table.Summary.Row>
            <Table.Summary.Row className="total-section">
              <Table.Summary.Cell className="boldText" colSpan={5}>
                <span>{t('totalWithVat')}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell className="boldText">
                {isLoading || isLoadingAfterRedeemingVoucher
                  ? <Skeleton />
                  : !shouldHideCartTotalPrice
                  ? (
                    <FormatPrice
                      currencyUnit={cartData?.currencyUnit}
                      price={cartData?.cartTotalVAT}
                    />
                  )
                  : null}
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        )}
      </>
    )
  }

  return (
    <B2becTable
      className="cart-table"
      dataSource={isLoading ? fakeProducts : cartDataSource}
      pagination={false}
      rowClassName={tableRowClasses}
      rowKey={cartRowKey}
      style={{ width: '100%' }}
      summary={tableSummary}
      columns={tableColumns({
        isLoading,
        handleCellClick,
        shouldShowRecyclingFee,
        t,
        confirmReplacementHandler,
        isInsideSupportArea,
        confirmMoqHandler,
        shouldDisplayVoucherLevel,
        currencyUnit: cartData?.currencyUnit,
        hasDeleteProductFromCartPermission,
        hasViewIndividualPricePermission,
      })}
    />
  )
}

export default CartItemTable
